import axios from "axios";
import { API_ENDPOINT as defaultApiBase } from "../config/apiEndpoint";

/**
 * Unified profile picture upload with fallback chain:
 * 1. S3 presigned URL (primary - best for scalability)
 * 2. Multipart/form-data (fallback - direct file upload, avoids base64 size issues)
 * 3. Base64 JSON (last resort - when multipart fails)
 *
 * @param {File} file - The image file to upload
 * @param {string} apiEndpoint - Base API URL (e.g. from process.env.REACT_APP_API_END_POINT)
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function uploadProfilePicture(file, apiEndpoint) {
  const base = apiEndpoint || defaultApiBase;
  const uploadConfig = { withCredentials: true };

  // --- 1. Try S3 presigned URL flow ---
  try {
    const urlRes = await axios.post(
      `${base}/api/UserProfile/GetProfilePictureUploadUrl`,
      { fileName: file.name, contentType: file.type },
      uploadConfig
    );

    if (urlRes.data?.status && urlRes.data.uploadUrl) {
      const { uploadUrl, s3Key, contentType } = urlRes.data;
      // Use plain fetch for S3 PUT — do NOT use axios or credentials.
      // S3 rejects credentialed cross-origin requests; Content-Type must match backend.
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType || file.type },
        credentials: "omit",
      });

      if (putRes.ok) {
        const confirmRes = await axios.post(
          `${base}/api/UserProfile/ConfirmProfilePictureUpload`,
          { s3Key, fileName: file.name, contentType },
          uploadConfig
        );
        if (confirmRes.data?.status) {
          return { success: true, message: confirmRes.data.message };
        }
      }
      // S3 PUT or confirm failed - fall through to fallback
    }
  } catch {
    // S3 not configured or GetProfilePictureUploadUrl failed - fall through to fallback
  }

  // --- 2. Try multipart/form-data (direct file upload) ---
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${base}/api/UserProfile/UploadProfilePicture`,
      formData,
      uploadConfig
    );
    if (res.data?.status) {
      return { success: true, message: res.data.message };
    }
    return { success: false, message: res.data?.message || "Upload failed." };
  } catch (error) {
    // Multipart failed - fall through to base64
  }

  // --- 3. Try base64 JSON (last resort) ---
  try {
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(file);
    });

    const res = await axios.post(
      `${base}/api/UserProfile/ConfirmProfilePictureUpload`,
      { fileName: file.name, contentType: file.type, fileData: base64 },
      uploadConfig
    );
    if (res.data?.status) {
      return { success: true, message: res.data.message };
    }
    return { success: false, message: res.data?.message || "Upload failed." };
  } catch (error) {
    const msg =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while uploading your profile picture.";
    return { success: false, message: msg };
  }
}
